import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Download } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
applyPlugin(jsPDF);

export const DACharges = ({ formData, setFormData, fsiBreakdown, societyCosts, fsiCosts, setDAChargesCosts }) => {
  const [chargesBreakdown, setChargesBreakdown] = useState(null);

  const calculateDACharges = () => {
    // Area conversions (sft to sq mtr)
    const conversionFactor = 0.092903;
    const areaFlatsValue = formData.hasFlats ? (parseFloat(formData.areaFlats) || 0) : 0;
    const areaOfficesValue = formData.hasOffices ? (parseFloat(formData.areaOffices) || 0) : 0;
    const areaShopsValue = formData.hasShops ? (parseFloat(formData.areaShops) || 0) : 0;
    
    const areaFlatsSqMts = areaFlatsValue * conversionFactor;
    const areaOfficesSqMts = areaOfficesValue * conversionFactor;
    const areaShopsSqMts = areaShopsValue * conversionFactor;
    let existingCarpetSqMts = areaFlatsSqMts + areaOfficesSqMts + areaShopsSqMts;

    // For Greenfield JV: Landowner's carpet area (JV %) = "existing area" for DA purposes
    const isGreenfield = formData.projectMode === "greenfield";
    const jvRatio = parseFloat(formData.jvRatioPercent) || 30;
    const jvRatioFlats = parseFloat(formData.jvRatioFlats) || jvRatio;
    const jvRatioOffices = parseFloat(formData.jvRatioOffices) || jvRatio;
    const jvRatioShops = parseFloat(formData.jvRatioShops) || jvRatio;
    
    let landownerCarpetSqMts = 0;
    if (isGreenfield && fsiBreakdown) {
      // Total carpet area from FSI
      const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
      const carpetFactor = parseFloat(formData.carpetAreaFactor) || 70;
      const totalCarpetArea = totalFSIArea * (carpetFactor / 100);

      // Weight by actual property-type share so an offices-only project uses only the offices JV %.
      const resPct = parseFloat(formData.residentialPercentage);
      const commPct = parseFloat(formData.commercialPercentage);
      const officesSplit = parseFloat(formData.saleableAreaSplitOffices) || 0;
      const shopsSplit = parseFloat(formData.saleableAreaSplitShops) || 0;
      const pt = formData.projectType;

      let flatsRatio = 0, officesRatio = 0, shopsRatio = 0;
      if (pt === "residential") {
        flatsRatio = 1;
      } else if (pt === "commercial") {
        const totalSplit = officesSplit + shopsSplit;
        if (totalSplit > 0) {
          officesRatio = officesSplit / totalSplit;
          shopsRatio = shopsSplit / totalSplit;
        } else {
          officesRatio = 1; // default all-offices when no split specified
        }
      } else if (pt === "mixed") {
        flatsRatio = (isNaN(resPct) ? 60 : resPct) / 100;
        const commercialFraction = (isNaN(commPct) ? (100 - (isNaN(resPct) ? 60 : resPct)) : commPct) / 100;
        const totalSplit = officesSplit + shopsSplit;
        if (totalSplit > 0) {
          officesRatio = commercialFraction * (officesSplit / totalSplit);
          shopsRatio = commercialFraction * (shopsSplit / totalSplit);
        } else {
          officesRatio = commercialFraction; // default all commercial → offices
        }
      } else {
        flatsRatio = 1;
      }

      const flatsCarpet = totalCarpetArea * flatsRatio;
      const officesCarpet = totalCarpetArea * officesRatio;
      const shopsCarpet = totalCarpetArea * shopsRatio;

      landownerCarpetSqMts =
        flatsCarpet * (jvRatioFlats / 100) +
        officesCarpet * (jvRatioOffices / 100) +
        shopsCarpet * (jvRatioShops / 100);

      // Use landowner's carpet as existing area for DA stamp duty
      existingCarpetSqMts = landownerCarpetSqMts;
    }

    // Additional free area calculation
    // If direct area offer mode, use that directly (convert sqft to sqm using /10.76)
    let additionalFreeCarpet = 0;
    let additionalFreePercent = 0;
    let totalCarpetToSociety = 0;
    const isDirectAreaMode = !isGreenfield && formData.areaOfferMode === "direct" && parseFloat(formData.directAreaOfferedSqft) > 0;

    // Terrace/Garden converted area (always calculated, used in percentage mode)
    const areaTerraceSqMts = (parseFloat(formData.areaTerrace) || 0) * conversionFactor;
    const areaGardenSqMts = (parseFloat(formData.areaGarden) || 0) * conversionFactor;
    const terraceConversionRatio = parseFloat(formData.terraceConversionRatio) || 25;
    const gardenConversionRatio = parseFloat(formData.gardenConversionRatio) || 25;
    const convertedTerrace = areaTerraceSqMts * (terraceConversionRatio / 100);
    const convertedGarden = areaGardenSqMts * (gardenConversionRatio / 100);
    const applyIncrementOnTG = formData.applyIncrementOnTerraceGarden === "yes";
    
    if (isDirectAreaMode) {
      // Direct mode: total area offered is the totalCarpetToSociety
      const directAreaSqft = parseFloat(formData.directAreaOfferedSqft) || 0;
      totalCarpetToSociety = directAreaSqft / 10.76; // sqft to sqm
      additionalFreeCarpet = totalCarpetToSociety - existingCarpetSqMts;
      additionalFreePercent = existingCarpetSqMts > 0 ? (additionalFreeCarpet / existingCarpetSqMts) * 100 : 0;
    } else {
      additionalFreePercent = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
      additionalFreeCarpet = existingCarpetSqMts * (additionalFreePercent / 100);

      const freePercentForIncrement = parseFloat(formData.freeAreaPercentFlats) || additionalFreePercent;
      const incrementOnTerrace = applyIncrementOnTG ? convertedTerrace * (freePercentForIncrement / 100) : 0;
      const incrementOnGarden = applyIncrementOnTG ? convertedGarden * (freePercentForIncrement / 100) : 0;
      const totalTerraceGardenWithIncrement = convertedTerrace + convertedGarden + incrementOnTerrace + incrementOnGarden;

      totalCarpetToSociety = existingCarpetSqMts + additionalFreeCarpet + totalTerraceGardenWithIncrement;
    }
    
    // Increment values for display (0 in direct mode)
    const freePercentForIncrement = parseFloat(formData.freeAreaPercentFlats) || additionalFreePercent;
    const incrementOnTerrace = (!isDirectAreaMode && applyIncrementOnTG) ? convertedTerrace * (freePercentForIncrement / 100) : 0;
    const incrementOnGarden = (!isDirectAreaMode && applyIncrementOnTG) ? convertedGarden * (freePercentForIncrement / 100) : 0;
    const totalTerraceGardenWithIncrement = isDirectAreaMode ? 0 : (convertedTerrace + convertedGarden + incrementOnTerrace + incrementOnGarden);

    // RR Rates
    const rrConstruction = parseFloat(formData.rrRateConstruction) || 0;
    const rrPlot = parseFloat(formData.rrRatePlot) || 0; // Land RR = Plot RR
    const numMembers = parseInt(formData.numMembers) || 0;
    const durationMonths = parseInt(formData.projectDuration) || 0;
    const durationYears = durationMonths / 12;

    // FSI values from fsiBreakdown
    const totalFSI = fsiBreakdown?.totalFSIWithAncillary || 0;
    const tdrArea = fsiBreakdown?.tdrArea || 0;
    const paidFSIArea = fsiBreakdown?.paidFSIArea || 0;
    const ancillaryArea = fsiBreakdown?.totalAncillary || 0;

    // ============ OPTION 1: DA CHARGES CALCULATION ============

    // A. Base Value = Total Carpet to Society × 1.1 × Construction RR Rate
    const baseValue = totalCarpetToSociety * 1.1 * rrConstruction;

    // B. Society Costs (from societyCosts prop)
    const totalRent = societyCosts?.totalRentalCost || 0;
    const monthlyRent = societyCosts?.monthlyRent || (totalRent / (parseInt(formData.constructionPeriod) || 36));
    const shiftingCharges = societyCosts?.shiftingCharges || 0;
    const corpusCharges = societyCosts?.corpusCharges || 0;
    const depositCost = societyCosts?.depositCost || 0;
    const bettermentCharges = societyCosts?.bettermentCharges || 0;

    // Deposit interest: Refundable Deposit × Duration (years) × 6%
    const depositInterest = depositCost * durationYears * 0.06;

    // C. Parking Cost
    const fourWheelerPerMember = parseFloat(formData.fourWheelerPerMember) || 1;
    const twoWheelerPerMember = parseFloat(formData.twoWheelerPerMember) || 1;
    
    // 4-Wheeler: (Members × 4W per member) × 12.5 sq mtr × 25% × Construction RR
    const total4WheelerParking = numMembers * fourWheelerPerMember;
    const fourWheelerCost = total4WheelerParking * 12.5 * 0.25 * rrConstruction;
    
    // 2-Wheeler: (Members × 2W per member) × 2 sq mtr × 25% × Construction RR
    const total2WheelerParking = numMembers * twoWheelerPerMember;
    const twoWheelerCost = total2WheelerParking * 2 * 0.25 * rrConstruction;
    
    const totalParkingCost = fourWheelerCost + twoWheelerCost;

    // D. Development Charges = Total Carpet to Society × 1.1 × 2% × Plot RR Rate
    const developmentCharges = totalCarpetToSociety * 1.1 * 0.02 * rrPlot;

    // E. Fungible Cost = Free Area × 1.1 × 0.3 × Land RR Rate
    const fungibleCost = additionalFreeCarpet * 1.1 * 0.3 * rrPlot;

    // TOTAL OPTION 1
    const option1Total = baseValue + totalRent + shiftingCharges + corpusCharges + 
                          depositInterest + totalParkingCost + developmentCharges + 
                          fungibleCost + bettermentCharges;

    // ============ OPTION 2: NEW FORMULA ============
    const isMhada = formData.projectClassification === "mhada";

    // (Total FSI - Existing Carpet × 1.1 - Free Area × 1.1 - Converted Terrace/Garden × 1.1) × Plot RR
    const fsiMinusCarpet = totalFSI - (existingCarpetSqMts * 1.1) - (additionalFreeCarpet * 1.1) - (totalTerraceGardenWithIncrement * 1.1);
    const option2Base = fsiMinusCarpet * rrPlot;
    
    let option2Total;
    let tdrDeduction = 0;
    let paidFSIDeduction = 0;
    let ancillaryDeduction = 0;
    let mhadaPremiumFSICost = 0;
    let titbitAreaCostDA = 0;

    if (isMhada) {
      // MHADA: No TDR, No Paid FSI deductions
      // Less MHADA premium FSI cost, less titbit area cost
      ancillaryDeduction = ancillaryArea * 0.15 * rrPlot;
      mhadaPremiumFSICost = fsiCosts?.mhadaFSICost || 0;
      titbitAreaCostDA = fsiCosts?.titbitAreaCost || 0;
      option2Total = Math.max(0, option2Base - mhadaPremiumFSICost - titbitAreaCostDA - ancillaryDeduction);
    } else {
      // Non-MHADA: TDR and Paid FSI deductions apply
      tdrDeduction = tdrArea * 0.30 * rrPlot;
      paidFSIDeduction = paidFSIArea * 0.35 * rrPlot;
      ancillaryDeduction = ancillaryArea * 0.15 * rrPlot;
      option2Total = Math.max(0, option2Base - tdrDeduction - paidFSIDeduction - ancillaryDeduction);
    }

    // ============ HIGHER OF TWO OPTIONS ============
    const higherOption = Math.max(option1Total, option2Total);
    const selectedOption = option1Total >= option2Total ? 1 : 2;

    // ============ STAMP DUTY & REGISTRATION ============
    // Stamp Duty = 5% of HIGHER option
    // Registration = ₹30,000 (fixed)
    const stampDuty = higherOption * 0.05;
    const registrationCharges = 30000;
    
    // Total DA Charges = Stamp Duty + Registration
    const totalDACharges = stampDuty + registrationCharges;

    const breakdown = {
      // Areas
      existingCarpetSqMts,
      totalExistingAreaSft: areaFlatsValue + areaOfficesValue + areaShopsValue, // in sft for display
      additionalFreeCarpet,
      additionalFreePercent,
      isDirectAreaMode,
      directAreaOfferedSqft: isDirectAreaMode ? parseFloat(formData.directAreaOfferedSqft) || 0 : 0,
      convertedTerrace,
      convertedGarden,
      incrementOnTerrace,
      incrementOnGarden,
      totalTerraceGardenWithIncrement,
      totalCarpetToSociety,
      // Base
      baseValue,
      rrConstruction,
      // Society costs
      totalRent,
      monthlyRent,
      constructionPeriod: parseInt(formData.constructionPeriod) || 36,
      constructionYears: durationYears,
      numMembers,
      shiftingCharges,
      corpusCharges,
      depositCost,
      depositInterest,
      bettermentCharges,
      // Parking
      total4WheelerParking,
      fourWheelerCost,
      total2WheelerParking,
      twoWheelerCost,
      totalParkingCost,
      // Other charges
      developmentCharges,
      fungibleCost,
      // Option 1
      option1Total,
      // Option 2
      isMhada,
      totalFSI,
      fsiMinusCarpet,
      option2Base,
      tdrArea,
      tdrDeduction,
      paidFSIArea,
      paidFSIDeduction,
      ancillaryArea,
      ancillaryDeduction,
      mhadaPremiumFSICost,
      titbitAreaCostDA,
      option2Total,
      // Final
      higherOption,
      selectedOption,
      stampDuty,
      registrationCharges,
      totalDACharges,
      // Legacy compatibility
      totalStampDuty: stampDuty,
      totalRegistration: registrationCharges
    };

    setChargesBreakdown(breakdown);

    // Pass to parent
    if (setDAChargesCosts) {
      setDAChargesCosts({ ...breakdown, _fromComponent: true });
    }
  };

  useEffect(() => {
    calculateDACharges();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fsiBreakdown,
    fsiCosts,
    societyCosts,
    formData.rrRatePlot,
    formData.rrRateConstruction,
    formData.areaFlats,
    formData.areaOffices,
    formData.areaShops,
    formData.areaTerrace,
    formData.areaGarden,
    formData.terraceConversionRatio,
    formData.gardenConversionRatio,
    formData.applyIncrementOnTerraceGarden,
    formData.hasFlats,
    formData.hasOffices,
    formData.hasShops,
    formData.additionalFreeAreaPercent,
    formData.numMembers,
    formData.projectDuration,
    formData.fourWheelerPerMember,
    formData.twoWheelerPerMember
  ]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value, decimals = 2) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const exportDACalculations = () => {
    if (!chargesBreakdown) return;
    const b = chargesBreakdown;
    const fc = (v) => formatCurrency(v);
    const fn = (v) => formatNumber(v);
    const projectName = formData.projectName || formData.societyName || "Project";

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`DA Charges Calculation`, pageWidth / 2, y, { align: "center" });
    y += 8;
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(projectName, pageWidth / 2, y, { align: "center" });
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, pageWidth / 2, y, { align: "center" });
    doc.setTextColor(0);
    y += 10;

    // Section: Carpet Area to Society
    doc.autoTable({
      startY: y,
      head: [["Carpet Area to Society", "Value"]],
      body: [
        ["Existing Carpet Area", `${fn(b.existingCarpetSqMts)} sq mtr`],
        [`Additional Free Area (${formData.additionalFreeAreaPercent || formData.freeAreaPercentFlats || 0}%)`, `${fn(b.additionalFreeCarpet)} sq mtr`],
        ...(b.convertedTerrace > 0 ? [[`Converted Terrace (@ ${formData.terraceConversionRatio || 25}%)`, `${fn(b.convertedTerrace)} sq mtr`]] : []),
        ...(b.incrementOnTerrace > 0 ? [["Increment on Terrace", `${fn(b.incrementOnTerrace)} sq mtr`]] : []),
        ...(b.convertedGarden > 0 ? [[`Converted Garden (@ ${formData.gardenConversionRatio || 25}%)`, `${fn(b.convertedGarden)} sq mtr`]] : []),
        ...(b.incrementOnGarden > 0 ? [["Increment on Garden", `${fn(b.incrementOnGarden)} sq mtr`]] : []),
        ["Total Carpet to Society", `${fn(b.totalCarpetToSociety)} sq mtr`],
      ],
      theme: "grid",
      headStyles: { fillColor: [30, 64, 175], fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Section: Option 1
    doc.autoTable({
      startY: y,
      head: [["Option 1: Itemised Method", "Amount"]],
      body: [
        [`Base Value (${fn(b.totalCarpetToSociety)} sqm x 1.1 x RR Plot Rs.${fn(b.rrRatePlot || 0)})`, fc(b.baseValue)],
        ["Rental", fc(b.totalRent)],
        ["Shifting Charges", fc(b.shiftingCharges)],
        ["Corpus Charges", fc(b.corpusCharges)],
        ["Deposit Interest", fc(b.depositInterest)],
        ["Betterment Charges", fc(b.bettermentCharges)],
        [`Parking (4W: ${b.total4WheelerParking} x Rs.${fn(b.fourWheelerCost || 0)} + 2W: ${b.total2WheelerParking} x Rs.${fn(b.twoWheelerCost || 0)})`, fc(b.totalParkingCost)],
        ["Development Charges", fc(b.developmentCharges)],
        ["Fungible Cost", fc(b.fungibleCost)],
      ],
      foot: [["OPTION 1 TOTAL", fc(b.option1Total)]],
      theme: "grid",
      headStyles: { fillColor: [30, 64, 175], fontSize: 10 },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Section: Option 2
    const option2Body = [
      [`Total FSI Area`, `${fn(b.totalFSI)} sq mtr`],
      ["Net Saleable x RR Plot", fc(b.option2Base)],
    ];
    if (b.isMhada) {
      option2Body.push(["Less: MHADA Premium FSI Cost", `-${fc(b.mhadaPremiumFSICost)}`]);
      if (b.titbitAreaCostDA > 0) {
        option2Body.push(["Less: Titbit Area Cost", `-${fc(b.titbitAreaCostDA)}`]);
      }
    } else {
      option2Body.push(["Less TDR Deduction", `-${fc(b.tdrDeduction)}`]);
      option2Body.push(["Less Paid FSI Deduction", `-${fc(b.paidFSIDeduction)}`]);
    }
    option2Body.push(["Less Ancillary Deduction", `-${fc(b.ancillaryDeduction)}`]);

    doc.autoTable({
      startY: y,
      head: [["Option 2: FSI-Based Method", "Amount"]],
      body: option2Body,
      foot: [["OPTION 2 TOTAL", fc(b.option2Total)]],
      theme: "grid",
      headStyles: { fillColor: [30, 64, 175], fontSize: 10 },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Section: Selected Option + Stamp Duty
    doc.autoTable({
      startY: y,
      head: [["Summary", "Amount"]],
      body: [
        [`Selected: Option ${b.selectedOption} (Higher)`, fc(b.higherOption)],
        [`Stamp Duty (${formData.stampDutyRate || 5}%)`, fc(b.stampDuty)],
        ["Registration", fc(b.totalRegistration)],
      ],
      foot: [["TOTAL DA CHARGES", fc(b.totalDACharges)]],
      theme: "grid",
      headStyles: { fillColor: [22, 163, 74], fontSize: 10 },
      footStyles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 11 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });

    doc.save(`DA_Charges_${projectName.replace(/\s+/g, '_')}.pdf`);
    toast.success("DA Charges exported as PDF");
  };

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <Card className="rounded-sm shadow-sm bg-secondary/30">
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">
            <strong>DA Charges</strong> = Stamp Duty (5% of higher option) + Registration (₹30,000)
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Higher option selected from:
          </p>
          <ul className="text-sm text-muted-foreground list-disc list-inside mt-1 space-y-1">
            <li><strong>Option 1:</strong> Base Value + Rent + Society Charges + Parking + Development + Fungible</li>
            <li><strong>Option 2:</strong> {formData.projectClassification === "mhada" 
              ? "(Total FSI - Carpet×1.1 - Free×1.1) × Plot RR - MHADA Premium - Titbit Cost - Ancillary@15%"
              : "(Total FSI - Carpet×1.1 - Free×1.1) × Plot RR - TDR@30% - Paid FSI@35% - Ancillary@15%"}</li>
          </ul>
        </CardContent>
      </Card>

      {/* DA Charges Breakdown */}
      {chargesBreakdown && (
        <Card className="rounded-sm shadow-sm border-accent/50" data-testid="da-charges-breakdown">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading tracking-tight text-accent">DA Charges Breakdown</CardTitle>
                <CardDescription>
                  Selected: <strong>Option {chargesBreakdown.selectedOption}</strong> ({formatCurrency(chargesBreakdown.higherOption)})
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={exportDACalculations}
                data-testid="export-da-btn"
              >
                <Download className="h-4 w-4 mr-1" />
                Export PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Area Summary */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                {formData.projectMode === "greenfield" ? "Landowner Carpet Area (for DA Calculation)" : "Carpet Area to Society"}
              </h4>
              <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                {formData.projectMode === "greenfield" ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Landowner Carpet (JV % of Total Carpet)</span>
                      <span className="font-mono">{formatNumber(chargesBreakdown.totalCarpetToSociety)} sq mtr</span>
                    </div>
                    {chargesBreakdown.landownerCarpetSqMts > 0 && (
                      <p className="text-xs text-muted-foreground">Treated as "existing area" for stamp duty calculation purposes</p>
                    )}
                  </>
                ) : chargesBreakdown.isDirectAreaMode ? (
                  <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Existing Carpet Area</span>
                  <span className="font-mono">{formatNumber(chargesBreakdown.existingCarpetSqMts)} sq mtr ({Math.round(chargesBreakdown.totalExistingAreaSft).toLocaleString('en-IN')} sft)</span>
                </div>
                <div className="flex justify-between p-2 bg-blue-50 rounded-sm">
                  <span className="text-blue-700 font-medium">Direct Area Offered</span>
                  <span className="font-mono text-blue-700 font-semibold">{Math.round(chargesBreakdown.directAreaOfferedSqft).toLocaleString('en-IN')} sft = {formatNumber(chargesBreakdown.totalCarpetToSociety)} sq mtr</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Back-calculated Free %</span>
                  <span className="font-mono">{chargesBreakdown.additionalFreePercent?.toFixed(1)}%</span>
                </div>
                  </>
                ) : (
                  <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Existing Carpet Area</span>
                  <span className="font-mono">{formatNumber(chargesBreakdown.existingCarpetSqMts)} sq mtr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Additional Free Area ({chargesBreakdown.additionalFreePercent?.toFixed(0) || formData.additionalFreeAreaPercent || 0}%)</span>
                  <span className="font-mono">{formatNumber(chargesBreakdown.additionalFreeCarpet)} sq mtr</span>
                </div>
                {chargesBreakdown.convertedTerrace > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Converted Terrace (@ {formData.terraceConversionRatio || 25}%)</span>
                    <span className="font-mono">{formatNumber(chargesBreakdown.convertedTerrace)} sq mtr</span>
                  </div>
                )}
                {chargesBreakdown.incrementOnTerrace > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Increment on Terrace ({formData.freeAreaPercentFlats || formData.additionalFreeAreaPercent}%)</span>
                    <span className="font-mono">{formatNumber(chargesBreakdown.incrementOnTerrace)} sq mtr</span>
                  </div>
                )}
                {chargesBreakdown.convertedGarden > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Converted Garden (@ {formData.gardenConversionRatio || 25}%)</span>
                    <span className="font-mono">{formatNumber(chargesBreakdown.convertedGarden)} sq mtr</span>
                  </div>
                )}
                {chargesBreakdown.incrementOnGarden > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Increment on Garden ({formData.freeAreaPercentFlats || formData.additionalFreeAreaPercent}%)</span>
                    <span className="font-mono">{formatNumber(chargesBreakdown.incrementOnGarden)} sq mtr</span>
                  </div>
                )}
                <Separator className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>{formData.projectMode === "greenfield" ? "Landowner Carpet Area (for DA)" : "Total Carpet to Society"}</span>
                  <span className="font-mono">{formatNumber(chargesBreakdown.totalCarpetToSociety)} sq mtr</span>
                </div>
                  </>
                )}
              </div>
            </div>

            <Separator />

            {/* Option 1 Breakdown */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                Option 1 {chargesBreakdown.selectedOption === 1 && <span className="text-chart-1">(SELECTED)</span>}
              </h4>
              <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Base Value
                    <span className="text-xs ml-1">({formatNumber(chargesBreakdown.totalCarpetToSociety)} sqm × 1.1 × ₹{parseInt(chargesBreakdown.rrConstruction || formData.rrRateConstruction || 0).toLocaleString('en-IN')})</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.baseValue)}</span>
                </div>
                {formData.projectMode !== "greenfield" && (
                <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Total Rent
                    <span className="text-xs ml-1">(₹{formatCurrency(chargesBreakdown.monthlyRent).replace('₹', '')} × {chargesBreakdown.constructionPeriod || formData.constructionPeriod || 36} months)</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.totalRent)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Shifting Charges
                    <span className="text-xs ml-1">(₹{parseInt(formData.shiftingCharges || 0).toLocaleString('en-IN')} × {chargesBreakdown.numMembers || formData.numMembers || 0} × 2)</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.shiftingCharges)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Corpus Charges
                    <span className="text-xs ml-1">(₹{parseInt(formData.corpusCharges || 0).toLocaleString('en-IN')} × {chargesBreakdown.numMembers || formData.numMembers || 0})</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.corpusCharges)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Deposit Interest
                    <span className="text-xs ml-1">(₹{formatCurrency(chargesBreakdown.depositCost).replace('₹', '')} × {chargesBreakdown.constructionYears?.toFixed(1) || Math.ceil((formData.constructionPeriod || 36) / 12)} yrs × 6%)</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.depositInterest)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    4-Wheeler Parking
                    <span className="text-xs ml-1">({chargesBreakdown.total4WheelerParking} × 12.5 sqm × 25% × ₹{parseInt(chargesBreakdown.rrConstruction || formData.rrRateConstruction || 0).toLocaleString('en-IN')})</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.fourWheelerCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    2-Wheeler Parking
                    <span className="text-xs ml-1">({chargesBreakdown.total2WheelerParking} × 2 sqm × 25% × ₹{parseInt(chargesBreakdown.rrConstruction || formData.rrRateConstruction || 0).toLocaleString('en-IN')})</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.twoWheelerCost)}</span>
                </div>
                </>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Development Charges
                    <span className="text-xs ml-1">({formatNumber(chargesBreakdown.totalCarpetToSociety)} × 1.1 × 2% × ₹{parseInt(formData.rrRatePlot || 0).toLocaleString('en-IN')})</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.developmentCharges)}</span>
                </div>
                {formData.projectMode !== "greenfield" && (
                <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Fungible Cost
                    <span className="text-xs ml-1">({formatNumber(chargesBreakdown.additionalFreeCarpet || 0)} × 1.1 × 30% × ₹{parseInt(formData.rrRatePlot || 0).toLocaleString('en-IN')})</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.fungibleCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Betterment Charges
                    {formData.bettermentChargesType === "per_sft" ? (
                      <span className="text-xs ml-1">(₹{parseInt(formData.bettermentCharges || 0).toLocaleString('en-IN')}/sft × {chargesBreakdown.totalExistingAreaSft?.toLocaleString('en-IN') || '0'} sft)</span>
                    ) : (
                      <span className="text-xs ml-1">(₹{parseInt(formData.bettermentCharges || 0).toLocaleString('en-IN')}/flat × {chargesBreakdown.numMembers || formData.numMembers || 0} flats)</span>
                    )}
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.bettermentCharges)}</span>
                </div>
                </>
                )}
                <Separator className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>Option 1 Total</span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.option1Total)}</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Option 2 Breakdown */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                Option 2 {chargesBreakdown.selectedOption === 2 && <span className="text-chart-1">(SELECTED)</span>}
              </h4>
              <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total FSI Area</span>
                  <span className="font-mono">{formatNumber(chargesBreakdown.totalFSI)} sq mtr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Less: Existing Carpet × 1.1
                    <span className="text-xs ml-1">({formatNumber(chargesBreakdown.existingCarpetSqMts)} × 1.1)</span>
                  </span>
                  <span className="font-mono text-destructive">-{formatNumber(chargesBreakdown.existingCarpetSqMts * 1.1)} sq mtr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Less: Free Area × 1.1
                    <span className="text-xs ml-1">({formatNumber(chargesBreakdown.additionalFreeCarpet)} × 1.1)</span>
                  </span>
                  <span className="font-mono text-destructive">-{formatNumber(chargesBreakdown.additionalFreeCarpet * 1.1)} sq mtr</span>
                </div>
                {chargesBreakdown.totalTerraceGardenWithIncrement > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Less: Terrace/Garden × 1.1
                      <span className="text-xs ml-1">({formatNumber(chargesBreakdown.totalTerraceGardenWithIncrement)} × 1.1)</span>
                    </span>
                    <span className="font-mono text-destructive">-{formatNumber(chargesBreakdown.totalTerraceGardenWithIncrement * 1.1)} sq mtr</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Net Area × Plot RR
                    <span className="text-xs ml-1">({formatNumber(chargesBreakdown.option2NetArea || (chargesBreakdown.totalFSI - (chargesBreakdown.existingCarpetSqMts * 1.1) - (chargesBreakdown.additionalFreeCarpet * 1.1) - (chargesBreakdown.totalTerraceGardenWithIncrement * 1.1 || 0)))} × ₹{parseInt(formData.rrRatePlot || 0).toLocaleString('en-IN')})</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.option2Base)}</span>
                </div>
                <Separator className="my-2" />
                {chargesBreakdown.isMhada ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Less: MHADA Premium FSI Cost
                        <span className="text-xs ml-1">(from FSI Costs)</span>
                      </span>
                      <span className="font-mono text-destructive">-{formatCurrency(chargesBreakdown.mhadaPremiumFSICost)}</span>
                    </div>
                    {chargesBreakdown.titbitAreaCostDA > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Less: Titbit Area Cost
                          <span className="text-xs ml-1">(from FSI Costs)</span>
                        </span>
                        <span className="font-mono text-destructive">-{formatCurrency(chargesBreakdown.titbitAreaCostDA)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Less: Ancillary
                        <span className="text-xs ml-1">({formatNumber(chargesBreakdown.ancillaryArea)} × 15% × ₹{parseInt(formData.rrRatePlot || 0).toLocaleString('en-IN')})</span>
                      </span>
                      <span className="font-mono text-destructive">-{formatCurrency(chargesBreakdown.ancillaryDeduction)}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Less: TDR
                        <span className="text-xs ml-1">({formatNumber(chargesBreakdown.tdrArea)} × 30% × ₹{parseInt(formData.rrRatePlot || 0).toLocaleString('en-IN')})</span>
                      </span>
                      <span className="font-mono text-destructive">-{formatCurrency(chargesBreakdown.tdrDeduction)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Less: Paid FSI
                        <span className="text-xs ml-1">({formatNumber(chargesBreakdown.paidFSIArea)} × 35% × ₹{parseInt(formData.rrRatePlot || 0).toLocaleString('en-IN')})</span>
                      </span>
                      <span className="font-mono text-destructive">-{formatCurrency(chargesBreakdown.paidFSIDeduction)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Less: Ancillary
                        <span className="text-xs ml-1">({formatNumber(chargesBreakdown.ancillaryArea)} × 15% × ₹{parseInt(formData.rrRatePlot || 0).toLocaleString('en-IN')})</span>
                      </span>
                      <span className="font-mono text-destructive">-{formatCurrency(chargesBreakdown.ancillaryDeduction)}</span>
                    </div>
                  </>
                )}
                <Separator className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>Option 2 Total</span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.option2Total)}</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* Stamp Duty & Registration */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">DA Charges Calculation</h4>
              <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Higher Option Value</span>
                  <span className="font-mono font-semibold">{formatCurrency(chargesBreakdown.higherOption)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Stamp Duty
                    <span className="text-xs ml-1">({formatCurrency(chargesBreakdown.higherOption)} × {chargesBreakdown.stampDutyRate || 5}%)</span>
                  </span>
                  <span className="font-mono">{formatCurrency(chargesBreakdown.stampDuty)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Registration Charges
                    <span className="text-xs ml-1">(Fixed @ ₹30,000)</span>
                  </span>
                  <span className="font-mono">₹30,000</span>
                </div>
              </div>
            </div>

            {/* Total DA Charges */}
            <div className="bg-accent/10 p-4 rounded-sm border border-accent/30">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold text-lg">Total DA Charges</span>
                  <p className="text-xs text-muted-foreground">Stamp Duty + Registration</p>
                </div>
                <span className="font-mono font-bold text-2xl text-accent">{formatCurrency(chargesBreakdown.totalDACharges)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!formData.rrRateConstruction && (
        <Card className="rounded-sm shadow-sm bg-secondary/50">
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>Please enter Construction RR Rate in the Property tab to calculate DA Charges</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DACharges;
